/**
 * @fileoverview "Send announcement" — tell a program's people something.
 *
 * Preset to everyone: every instructor in the program and its viewers, which
 * is who "an announcement about this program" is for. "Choose" narrows it to
 * named instructors, the instructors of named classes, and (optionally) the
 * viewers. Choosing only classes makes it a CLASS announcement — switched
 * separately in the org's Notifications settings from a program one.
 *
 * Every instructor it reaches reads it in their Updates on Hans, signed by
 * this admin and the organization. Email and text are extra channels, offered
 * only where the Notifications settings allow them: an unticked-and-disabled
 * box says why rather than disappearing, so an admin can tell "off" from
 * "not possible". Viewers are reached by email only.
 *
 * Opened from a class page it arrives preset to that class alone.
 */

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, SegmentedControl } from "@/components/studio/primitives";

const MAX_MESSAGE = 2000;

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

/** Which settings section a selection is sent under (mirrors the server). */
export function announcementKind({ audience, instructorIds, classIds }) {
  return audience === "selected" && classIds.length > 0 && instructorIds.length === 0
    ? "class_announcement"
    : "program_announcement";
}

/** "Sent to 4 instructors · 5 emails · 2 texts." plus anyone a text could not reach. */
export function sentSummary(result) {
  const parts = [];
  const n = result?.instructors_reached ?? 0;
  parts.push(`Sent to ${n} instructor${n === 1 ? "" : "s"}`);
  if (result?.viewers) parts.push(`${result.viewers} viewer${result.viewers === 1 ? "" : "s"}`);
  if (result?.emailed) parts.push(`${result.emailed} email${result.emailed === 1 ? "" : "s"}`);
  if (result?.texted) parts.push(`${result.texted} text${result.texted === 1 ? "" : "s"}`);
  let line = `${parts.join(" · ")}.`;
  if (result?.no_phone) {
    line += ` ${result.no_phone} instructor${result.no_phone === 1 ? " has" : "s have"} no phone number for texts.`;
  }
  return line;
}

function Check({ checked, onChange, label, sub, testId, disabled }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="mt-1 accent-[var(--accent-yellow)]"
      />
      <span className="min-w-0">
        <span className={`font-sans text-[14px] ${disabled ? "text-[var(--text-tertiary)]" : "text-[var(--text-primary)]"}`}>
          {label}
        </span>
        {sub && <span className="block font-sans text-[12px] text-[var(--text-tertiary)]">{sub}</span>}
      </span>
    </label>
  );
}

export default function AnnouncementDialog({
  open,
  onOpenChange,
  programId,
  programTitle,
  instructors = [],
  classes = [],
  viewerCount = 0,
  presetClassId = null,
  onSent,
}) {
  const [audience, setAudience] = useState("all");
  const [instructorIds, setInstructorIds] = useState(() => new Set());
  const [classIds, setClassIds] = useState(() => new Set());
  const [includeViewers, setIncludeViewers] = useState(true);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(false);
  const [sms, setSms] = useState(false);
  const [settings, setSettings] = useState(null);
  // The settings could not be read: the channels say so instead of "Checking".
  const [settingsFailed, setSettingsFailed] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAudience(presetClassId ? "selected" : "all");
    setInstructorIds(new Set());
    setClassIds(new Set(presetClassId ? [presetClassId] : []));
    setIncludeViewers(!presetClassId);
    setSubject("");
    setMessage("");
    setSms(false);
    setSettingsFailed(false);
    orgApi
      .get("/org/notification-settings")
      .then((res) => setSettings(res.data))
      .catch(() => {
        setSettings(null);
        setSettingsFailed(true);
      });
  }, [open, presetClassId]);

  const kind = announcementKind({ audience, instructorIds: [...instructorIds], classIds: [...classIds] });
  const section = settings?.settings?.[kind];
  const emailAllowed = Boolean(section?.enabled && section?.email);
  const smsAllowed = Boolean(section?.enabled && section?.sms && settings?.sms_available);

  // Tick email by default wherever the org sends it: that is what the switch
  // in Settings says it wants. Text stays a deliberate choice per message.
  useEffect(() => {
    setEmail(emailAllowed);
    if (!smsAllowed) setSms(false);
  }, [emailAllowed, smsAllowed]);

  const flip = (setter) => (id) => (on) =>
    setter((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const selectedCount = useMemo(() => {
    if (audience === "all") return instructors.length;
    const people = new Set(instructorIds);
    for (const c of classes) if (classIds.has(c.class_id)) (c.instructor_ids || []).forEach((u) => people.add(u));
    return people.size;
  }, [audience, instructors, instructorIds, classes, classIds]);

  // The same reasons as iOS (mobile/src/lib/orgAnnouncements.js channelReason).
  const why = (allowed, label) => {
    if (allowed) return null;
    if (!settings) return settingsFailed ? "Couldn't load your notification settings." : "Checking your Notifications settings…";
    if (label === "Text" && section?.enabled && section?.sms && !settings.sms_available) return settings.sms_unavailable_reason;
    return `${label} is off for ${kind === "class_announcement" ? "class" : "program"} announcements in Notifications.`;
  };

  const nobody = audience === "selected" && instructorIds.size === 0 && classIds.size === 0 && !includeViewers;

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Write the announcement before sending it");
      return;
    }
    if (nobody) {
      toast.error("Choose who this announcement is for");
      return;
    }
    setSending(true);
    try {
      const res = await orgApi.post(`/org/programs/${programId}/announcements`, {
        message: message.trim(),
        subject: subject.trim() || null,
        audience,
        instructor_ids: audience === "selected" ? [...instructorIds] : [],
        class_ids: audience === "selected" ? [...classIds] : [],
        include_viewers: audience === "all" ? true : includeViewers,
        email: email && emailAllowed,
        sms: sms && smsAllowed,
      });
      toast.success(sentSummary(res.data));
      onSent?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't send that announcement"));
    } finally {
      setSending(false);
    }
  };

  const viewerLine = viewerCount
    ? `and ${viewerCount} viewer${viewerCount === 1 ? "" : "s"}`
    : "(no viewers on this program yet)";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">Send an announcement</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} data-testid="announcement-form">
          {!presetClassId && (
            <SegmentedControl
              testId="announcement-audience"
              value={audience}
              onChange={setAudience}
              options={[
                { id: "all", label: "Everyone", testId: "announcement-audience-all" },
                { id: "selected", label: "Choose", testId: "announcement-audience-selected" },
              ]}
            />
          )}

          {audience === "all" ? (
            <p className="mt-3 font-sans text-[14px] text-[var(--text-secondary)]" data-testid="announcement-everyone">
              Every instructor in {programTitle || "this program"} ({instructors.length}) {viewerLine}.
            </p>
          ) : (
            <div className="mt-4 grid gap-5 sm:grid-cols-2">
              {instructors.length > 0 && (
                <div>
                  <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">Instructors</div>
                  <div className="flex max-h-48 flex-col gap-2 overflow-y-auto">
                    {instructors.map((m) => (
                      <Check
                        key={m.user_id}
                        checked={instructorIds.has(m.user_id)}
                        onChange={flip(setInstructorIds)(m.user_id)}
                        label={m.name || "Instructor"}
                        testId={`announce-instructor-${m.user_id}`}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div>
                {classes.length > 0 && (
                  <>
                    <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">Classes</div>
                    <div className="flex max-h-48 flex-col gap-2 overflow-y-auto">
                      {classes.map((c) => (
                        <Check
                          key={c.class_id}
                          checked={classIds.has(c.class_id)}
                          onChange={flip(setClassIds)(c.class_id)}
                          label={c.name}
                          sub={c.instructor_name || "No instructor yet"}
                          testId={`announce-class-${c.class_id}`}
                        />
                      ))}
                    </div>
                  </>
                )}
                {/* A class's own page is about its instructors; the program's
                    viewers are chosen from the program page. */}
                {!presetClassId && (
                  <div className="mt-4">
                    <Check
                      checked={includeViewers}
                      onChange={setIncludeViewers}
                      label={`Program viewers (${viewerCount})`}
                      sub="By email"
                      disabled={viewerCount === 0}
                      testId="announce-viewers"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value.slice(0, 150))}
            placeholder="Subject (optional)"
            aria-label="Subject"
            data-testid="announcement-subject"
            className={`${inputClass} mt-5`}
          />
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
            placeholder="What do you want them to know?"
            aria-label="Announcement"
            rows={6}
            data-testid="announcement-message"
            className={`${inputClass} mt-3 resize-y`}
          />
          <div className="mt-1 text-right font-mono text-[11px] text-[var(--text-tertiary)]">
            {message.length}/{MAX_MESSAGE}
          </div>

          <div className="mt-3">
            <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">Send by</div>
            <div className="flex flex-col gap-2">
              <Check checked disabled label="Updates on Hans" sub="Always, for every instructor it reaches." testId="announce-channel-updates" onChange={() => {}} />
              <Check
                checked={email && emailAllowed}
                disabled={!emailAllowed}
                onChange={setEmail}
                label="Email"
                sub={why(emailAllowed, "Email")}
                testId="announce-channel-email"
              />
              <Check
                checked={sms && smsAllowed}
                disabled={!smsAllowed}
                onChange={setSms}
                label="Text message"
                sub={why(smsAllowed, "Text") || "To instructors with a phone number on their profile."}
                testId="announce-channel-sms"
              />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={sending || !message.trim() || nobody} data-testid="announcement-send">
              {sending ? "Sending…" : audience === "all" ? "Send to everyone" : `Send${selectedCount ? ` to ${selectedCount}` : ""}`}
            </Button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
            >
              Cancel
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
